import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/authHeaders';
import { mayDo, useExperience } from '../experience/experience';
import { forgetDevice, readDeviceInfo, readDeviceToken, rememberDevice, DEFAULT_DEVICE_IDLE_MINUTES } from './kitchenDevice';

// Settings, Security: your kitchen PIN, and (owner, admin, manager) this browser as a kitchen device
// (backend/kitchen_devices.py, G-AUTH-2). The server decides; this only stops offering what it would refuse.
export const DEVICE_MANAGERS = ['org_owner', 'org_admin', 'manager'];     // kitchen_devices.REGISTER_ROLES

const CARD = 'bg-white rounded-xl border p-5 space-y-3';
const INPUT = 'w-full border rounded-lg px-3 py-2';
const PRIMARY = 'px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50';
const SECONDARY = 'px-3 py-1.5 rounded-lg border text-sm';
const messageOf = (e) => (typeof e?.detail === 'object' && e.detail?.message) || e?.message || 'That did not work.';

function MyPin() {
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiFetch('/api/platform/kitchen-pin', { requireOrg: true })
    .then(setStatus).catch(() => setStatus(null)), []);
  useEffect(() => { load(); }, [load]);
  const valid = /^\d{4,6}$/.test(pin) && pin === confirm && password;
  const save = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setNote('');
    try {
      await apiFetch('/api/platform/kitchen-pin', { method: 'PUT', requireOrg: true,
        body: JSON.stringify({ pin, password }) });
      setPin(''); setConfirm(''); setPassword('');
      setNote('Your kitchen PIN is set.');
      load();
    } catch (e) {
      setNote(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className={CARD} onSubmit={save} data-testid="kitchen-pin-settings">
      <h2 className="text-lg font-semibold">Kitchen PIN</h2>
      <p className="text-sm text-gray-600" data-testid="kitchen-pin-status">
        {status?.set ? 'You have a kitchen PIN.' : 'You have no kitchen PIN yet.'} It signs you in on this
        organisation&apos;s registered kitchen tablets only, never on the normal sign-in page.
      </p>
      <input className={INPUT} type="password" inputMode="numeric" maxLength={6} placeholder="New PIN (4 to 6 digits)"
             value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} data-testid="kitchen-pin-new"
             aria-label="New PIN" />
      <input className={INPUT} type="password" inputMode="numeric" maxLength={6} placeholder="The PIN again"
             value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
             data-testid="kitchen-pin-confirm" aria-label="Confirm PIN" />
      <input className={INPUT} type="password" autoComplete="current-password" placeholder="Your password"
             value={password} onChange={(e) => setPassword(e.target.value)} data-testid="kitchen-pin-password"
             aria-label="Your password" />
      <button type="submit" className={PRIMARY} disabled={!valid || busy} data-testid="kitchen-pin-save">
        {status?.set ? 'Change my PIN' : 'Set my PIN'}
      </button>
      {note && <p role="status" className="text-sm" data-testid="kitchen-pin-message">{note}</p>}
    </form>
  );
}

function Devices() {
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState('');
  const [name, setName] = useState('');
  const [idle, setIdle] = useState(String(DEFAULT_DEVICE_IDLE_MINUTES));
  const [here, setHere] = useState(readDeviceToken() ? readDeviceInfo() : null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiFetch('/api/platform/kitchen-devices', { requireOrg: true })
    .then((b) => setDevices(b.devices || [])).catch(() => setDevices([])), []);
  useEffect(() => {
    load();
    apiFetch('/api/locations', { requireOrg: true }).then((rows) => setSites(Array.isArray(rows) ? rows : []))
      .catch(() => setSites([]));
  }, [load]);
  const register = async (event) => {
    event.preventDefault();
    if (!site || !name.trim() || busy) return;
    setBusy(true);
    setNote('');
    try {
      const body = await apiFetch('/api/platform/kitchen-devices', { method: 'POST', requireOrg: true,
        body: JSON.stringify({ location_id: site, name: name.trim(), idle_lock_minutes: Number(idle) || DEFAULT_DEVICE_IDLE_MINUTES }) });
      rememberDevice(body.device_token, body.device);
      setHere(body.device);
      setName('');
      setNote('This browser is now a kitchen device. Its sign-in page is /kitchen.');
      load();
    } catch (e) {
      setNote(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (device) => {
    setBusy(true);
    try {
      await apiFetch(`/api/platform/kitchen-devices/${encodeURIComponent(device.id)}/revoke`, { method: 'POST', requireOrg: true });
      if (here?.id === device.id) { forgetDevice(); setHere(null); }
      load();
    } catch (e) {
      setNote(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={CARD} data-testid="kitchen-devices">
      <h2 className="text-lg font-semibold">Kitchen devices</h2>
      <p className="text-sm text-gray-600">
        A shared tablet registered for a site lets its members sign in with their PIN and locks after it is left idle.
      </p>
      {here ? (
        <p className="text-sm" data-testid="kitchen-device-here">
          This browser is the kitchen device <strong>{here.name}</strong> ({here.location_name || here.location_id}).{' '}
          <button type="button" className={SECONDARY} data-testid="kitchen-device-forget"
                  onClick={() => { forgetDevice(); setHere(null); }}>Forget on this browser</button>
        </p>
      ) : (
        <form onSubmit={register} className="space-y-2" data-testid="kitchen-device-register-form">
          <select className={INPUT} value={site} onChange={(e) => setSite(e.target.value)} data-testid="kitchen-device-site"
                  aria-label="Site">
            <option value="">Choose the site</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
          </select>
          <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
                 placeholder="Device name, e.g. Pass tablet" data-testid="kitchen-device-name" aria-label="Device name" />
          <label className="text-sm block">Lock after idle (minutes)
            <input className={INPUT} type="number" min={1} max={60} value={idle} onChange={(e) => setIdle(e.target.value)}
                   data-testid="kitchen-device-idle" />
          </label>
          <button type="submit" className={PRIMARY} disabled={busy || !site || !name.trim()}
                  data-testid="kitchen-device-register">Register this browser</button>
        </form>
      )}
      {note && <p role="status" className="text-sm" data-testid="kitchen-device-message">{note}</p>}
      <ul className="divide-y" data-testid="kitchen-device-list">
        {devices.map((d) => (
          <li key={d.id} className="py-2 flex items-center justify-between gap-2" data-testid={`kitchen-device-${d.id}`}>
            <span className="text-sm">{d.name} · {d.location_name || d.location_id} · locks after {d.idle_lock_minutes} min
              {d.revoked_at ? ' · revoked' : ''}</span>
            {!d.revoked_at && (
              <button type="button" className={SECONDARY} disabled={busy} onClick={() => revoke(d)}
                      data-testid={`kitchen-device-revoke-${d.id}`}>Revoke</button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function KitchenDevicePanel() {
  const exp = useExperience();
  if (exp?.status !== 'ready') return null;
  return (
    <div className="max-w-xl mx-auto px-4 pb-8 space-y-6" data-testid="kitchen-device-panel">
      <MyPin />
      {mayDo(exp, DEVICE_MANAGERS) && <Devices />}
    </div>
  );
}
